import { useEffect, useState } from 'react';
import {
  startTokenPrefetch,
  retryTokenFetch,
  getPrefetchState,
  getTokenFromCache,
  PrefetchState
} from '../lib/token-prefetch';
import { isIOSWebViewGold } from '../lib/webviewgold-detection';
import { logPush } from '../lib/push-diagnostics';

let prefetchInitialized = false;

export function useTokenPrefetch() {
  const [prefetchState, setPrefetchState] = useState<PrefetchState>('idle');
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    if (!isIOSWebViewGold()) {
      logPush('info', 'prefetch', 'useTokenPrefetch: Not iOS WebViewGold, skipping');
      return;
    }

    if (prefetchInitialized) {
      logPush('info', 'prefetch', 'useTokenPrefetch: Already initialized, using existing state');
      setPrefetchState(getPrefetchState());
      setToken(getTokenFromCache());
      return;
    }

    prefetchInitialized = true;
    logPush('info', 'prefetch', 'useTokenPrefetch: Initializing token prefetch on app mount');

    startTokenPrefetch().then((fetchedToken) => {
      setPrefetchState(getPrefetchState());
      setToken(fetchedToken);

      if (fetchedToken) {
        logPush('success', 'prefetch', 'useTokenPrefetch: Token prefetch completed successfully');
      } else {
        logPush('error', 'prefetch', 'useTokenPrefetch: Token prefetch failed');
      }
    });

    const checkInterval = setInterval(() => {
      const currentState = getPrefetchState();
      const currentToken = getTokenFromCache();

      setPrefetchState(currentState);
      setToken(currentToken);

      if (currentState === 'success' || currentState === 'failed') {
        clearInterval(checkInterval);
      }
    }, 1000);

    return () => {
      clearInterval(checkInterval);
    };
  }, []);

  const retry = async () => {
    logPush('info', 'retry', 'useTokenPrefetch: Manual retry triggered');
    const fetchedToken = await retryTokenFetch();
    setPrefetchState(getPrefetchState());
    setToken(fetchedToken);
    return fetchedToken;
  };

  return {
    prefetchState,
    token,
    retryPrefetch: retry,
    isIOSWebViewGold: isIOSWebViewGold()
  };
}
